import { t } from 'ttag'
import { useMutation, useQueryClient } from 'react-query'
import { useDispatch } from 'react-redux'
import { useState } from 'react'

import { Grid } from '@h2/components/containers/grid'
import { Text } from '@h2/components/typography/text'
import { api } from '@h2/api'
import { showUserFeedback } from '@h2/common/actions/showUserFeedback'
import { TrashIcon } from '@h2/components/icons'
import { Button } from '@h2/components/buttons/button'
import { Link } from '@h2/common/components/link'

import { UserSearch } from '../components/userSearch'

const Members = ({ group }) => {
  const members = group.members
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const [deleteId, setDeleteId] = useState(null)
  const { mutate: removeUser, isLoading } = useMutation(
    userToRemove => {
      setDeleteId(userToRemove)

      return api.fetch(`/admin/users/groups/removeMember`, {
        method: 'POST',
        version: 'v1',
        body: JSON.stringify({ userId: userToRemove, groupId: group.id })
      })
    },
    {
      onSuccess: rslt => {
        setDeleteId(null)
        queryClient.setQueriesData(['groups'], oldData => {
          if (!oldData) {
            return oldData
          }

          return oldData.map(record => {
            if (rslt.groupId === record.id) {
              return {
                ...record,
                members: record?.members.filter(member => member.id !== rslt.userId)
              }
            }

            return record
          })
        })
      },
      onError: () => {
        setDeleteId(null)
        dispatch(showUserFeedback(t`Could not remove member from group`, 'error'))
      }
    }
  )
  const { mutate: addMember, isLoading: isAdding } = useMutation(
    userToAdd => {
      return api.fetch(`/admin/users/groups/addMember`, {
        method: 'POST',
        version: 'v1',
        body: JSON.stringify({ userId: userToAdd, groupId: group.id })
      })
    },
    {
      onSuccess: rslt => {
        queryClient.setQueriesData(['groups'], oldData => {
          if (!oldData) {
            return oldData
          }

          return oldData.map(record => {
            if (rslt.groupId === record.id) {
              return {
                ...record,
                members: [
                  ...(record?.members ?? []),
                  {
                    ...rslt.user,
                    created: rslt.created
                  }
                ]
              }
            }

            return record
          })
        })
      },
      onError: () => {
        dispatch(showUserFeedback(t`Could not add member to group`, 'error'))
      }
    }
  )
  const columns = [
    {
      id: 'name',
      label: t`Username`,
      render({ id, ssoName, name }) {
        return <Link href={`/management/users/${id}/groups`}>{ssoName || name}</Link>
      }
    },
    {
      id: 'firstName',
      label: t`First Name`
    },
    {
      id: 'lastName',
      label: t`Last Name`
    },
    {
      id: 'dateCreated',
      label: t`Date Created`,
      render({ created }) {
        return new Date(created).toLocaleDateString()
      }
    },
    {
      id: 'id',
      label: t`Action`,
      width: '125px',
      sortable: false,
      render({ id, name }) {
        return (
          <Button
            icon={TrashIcon}
            onClick={() => {
              if (
                confirm(
                  t`Are you sure you want to remove ${name} as a member of ${group.name}?`
                )
              ) {
                removeUser(id)
              }
            }}
            display="red"
            size="small"
            align="right"
            isLoading={isLoading && deleteId === id}
            variant="outlined">
            {t`Remove`}
          </Button>
        )
      }
    }
  ]
  const listing = () => {
    if (!members?.length) {
      return (
        <Text margin="60px" size="large" align="center">
          {t`There are no members currently in this group.`}
        </Text>
      )
    }

    return (
      <Grid
        data={members}
        columns={columns}
        options={{
          isSearchQueryHighlighted: true,
          disableFilters: true,
          fullWidth: true
        }}
        rowIdKey="id"
        fullWidth={true}
        defaultPageSize={50}
        isRowSelectable={false}
        dateRangePicker={true}
      />
    )
  }

  return (
    <>
      {listing()}
      <UserSearch addFn={addMember} isAdding={isAdding} />
    </>
  )
}

export { Members }
